import {
  extractProvinceFromLocation,
  formatCorridorDisplay,
  parseCorridorToProvinces,
} from "@/shared/utils/routeCorridorUtils.js";

export const UNASSIGNED_TRIP_KEY = "__unassigned__";

function normalizeCorridorToken(routeCorridor) {
  if (!routeCorridor || routeCorridor === "UNKNOWN") {
    return null;
  }

  const display = formatCorridorDisplay(routeCorridor);
  if (display) return display;
  return String(routeCorridor).trim();
}

/**
 * Resolve a stable corridor key for an order (province pair or multi-hop corridor).
 * Linked trip corridor is the source of truth when available.
 * @param {Object|null|undefined} order
 * @param {{ trip?: Object|null }} [options]
 * @returns {string}
 */
export function resolveOrderCorridor(order, { trip } = {}) {
  const fromTrip = normalizeCorridorToken(trip?.routeCorridor);
  if (fromTrip) return fromTrip;

  const fromOrder = normalizeCorridorToken(order?.routeCorridor);
  if (fromOrder) return fromOrder;

  const pickup = extractProvinceFromLocation(order?.pickup);
  const delivery = extractProvinceFromLocation(order?.delivery);
  if (pickup && delivery) {
    return `${pickup}-${delivery}`;
  }

  const provinces = parseCorridorToProvinces(order?.routeCorridor);
  if (provinces.length >= 2) {
    return provinces.join("-");
  }

  return "Unknown corridor";
}

/**
 * Resolve corridor using a loaded trips map (trip first, then order fallbacks).
 * @param {Object|null|undefined} order
 * @param {Record<string, Object>} [tripsMap]
 * @returns {string}
 */
export function resolveOrderCorridorWithTripsMap(order, tripsMap = {}) {
  const trip = order?.tripId ? tripsMap[order.tripId] || null : null;
  return resolveOrderCorridor(order, { trip });
}

/**
 * Human-readable corridor label (origin → destination).
 * @param {string|null|undefined} corridorKey
 * @returns {string}
 */
export function formatCorridorGroupLabel(corridorKey) {
  if (!corridorKey || corridorKey === "Unknown corridor") {
    return "Unknown corridor";
  }

  const parts = corridorKey.split("-").filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0]} → ${parts[parts.length - 1]}`;
  }
  return corridorKey;
}

/**
 * Group orders by corridor, then by allocated trip (unassigned last).
 * @param {Object[]} orders
 * @param {{ tripsMap?: Record<string, Object> }} [options]
 * @returns {Array<{ key: string, label: string, totalCount: number, tripGroups: Object[] }>}
 */
export function groupOrdersByCorridorAndTrip(orders, { tripsMap = {} } = {}) {
  const corridors = {};

  for (const order of orders) {
    const trip = order.tripId ? tripsMap[order.tripId] || null : null;
    const corridorKey = resolveOrderCorridor(order, { trip });
    if (!corridors[corridorKey]) {
      corridors[corridorKey] = {
        key: corridorKey,
        label: formatCorridorGroupLabel(corridorKey),
        trips: {},
        totalCount: 0,
      };
    }

    const tripKey = order.tripId || UNASSIGNED_TRIP_KEY;
    if (!corridors[corridorKey].trips[tripKey]) {
      corridors[corridorKey].trips[tripKey] = {
        tripKey,
        tripId: order.tripId || null,
        trip: order.tripId ? tripsMap[order.tripId] || null : null,
        orders: [],
      };
    }

    corridors[corridorKey].trips[tripKey].orders.push(order);
    corridors[corridorKey].totalCount += 1;
  }

  return Object.values(corridors)
    .sort((a, b) => a.key.localeCompare(b.key))
    .map((corridor) => {
      const tripGroups = Object.values(corridor.trips).sort((a, b) => {
        if (a.tripKey === UNASSIGNED_TRIP_KEY) return 1;
        if (b.tripKey === UNASSIGNED_TRIP_KEY) return -1;

        const depA = a.trip?.departureDate
          ? new Date(a.trip.departureDate).getTime()
          : 0;
        const depB = b.trip?.departureDate
          ? new Date(b.trip.departureDate).getTime()
          : 0;
        return depA - depB || a.tripKey.localeCompare(b.tripKey);
      });

      return { ...corridor, tripGroups };
    });
}
